import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ElectionService } from 'src/app/services/election.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { IReportFirstPreference } from 'src/app/interfaces/IReportFirstPreference';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { LcFinalResultsService } from 'src/app/services/lc-final-results.service';
import { LcUtilsService } from 'src/app/services/lc-utils.service';


@Component({
  selector: 'app-lc-candidate-elected-table',
  templateUrl: './lc-candidate-elected-table.component.html',
  styleUrls: ['./lc-candidate-elected-table.component.scss']
})
export class LcCandidateElectedTableComponent  implements AfterViewInit, OnDestroy {

  @Input() isSmallTable = true;

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public LCChangeCandidateData: any[] = [];

  public dataTable: IReportFirstPreference[] = [];
  public dataSource = new MatTableDataSource<IReportFirstPreference>();
  public columns: ITableHeader[] = [];
  public displayedColumns: string[] = [];
  public dataIsLoaded = true;

  constructor(
    private electionService: ElectionService,
    private lcFinalResultsService: LcFinalResultsService,
    private lcUtils: LcUtilsService
  ) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private loadChart(page: ISelectPage): void {
    this.columns = [];
    this.LCChangeCandidateData = [];
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    const isFinal = election?.lcChange?.electionStatus === ElectionStatusEnum.final;

    if (page.pageType === PageTypeEnum.CountLCStateWide) {
      const parties = election?.lcElectionCount?.parties;

      parties?.forEach(party => {
        const candidates = party.candidates;
        if(candidates.length > 0) {
          candidates.forEach(c => {

            const candidateReElected: string = c.reElected ? '*' : ''

            this.LCChangeCandidateData.push({
              candidateName: `${candidateReElected}${c.name}`,
              party: party.partyName,
              colourCode: party.colourCode,
              orderElected: c.orderElected,
              countNumber: this.lcUtils.numberWithCommas(c.countNumber),
              votes: this.lcUtils.numberWithCommas(c.votes)
            })
          })
        }
      });

      // Set up columns
      this.setupColumns();

      // If election is final and we don't have candidate data, load from CSV
      // This ensures "Elected Members of Parliament" table is created automatically
      if (isFinal && this.LCChangeCandidateData.length === 0) {
        console.log('Automatically creating Elected Members of Parliament table from CSV for election:', page.electionDateString);
        this.loadFinalResultsFromCSV(page.electionDateString ?? '');
      } else {
        this.finalizeDataSource();
      }
    }
  }

  /**
   * Set up table columns
   */
  private setupColumns(): void {
    this.columns = [];
    
    this.columns.push({
      column: 'colourCode',
      label: '',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 20,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: true,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'candidateName',
      label: 'Candidate',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 160,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'party',
      label: 'Party',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 160,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'orderElected',
      label: 'Order',
      labelTwo: 'Elected',
      isTwoRowLabel: true,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'countNumber',
      label: `Count`,
      labelTwo: `Number`,
      isTwoRowLabel: true,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'votes',
      label: `Votes`,
      labelTwo: ``,
      isTwoRowLabel: true,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
  }

  /**
   * Finalize the data source after loading data
   */
  private finalizeDataSource(): void {
    this.LCChangeCandidateData.sort((a, b) => a.orderElected - b.orderElected);
    this.dataTable = this.LCChangeCandidateData;
    this.displayedColumns = this.columns.map(c => c.column);
    
    if (this.LCChangeCandidateData?.length > 0) {
      this.dataSource = new MatTableDataSource<IReportFirstPreference>(this.LCChangeCandidateData);
    }
  }

  /**
   * Load final election results from CSV file
   */
  private loadFinalResultsFromCSV(electionDateString: string): void {
    this.lcFinalResultsService.loadOrderOfElectionReport$(electionDateString)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (finalResults) => {
          if (finalResults && finalResults.length > 0) {
            console.log('Successfully loaded final candidate data from CSV:', finalResults.length, 'records');
            
            // Get elected candidates
            const electedCandidates = this.lcFinalResultsService.getElectedCandidates(finalResults);
            
            // Get election data for party colors
            const election = this.electionService.electionMap.get(electionDateString);
            const staticParties = election?.lcStatic?.parties || [];
            
            // Build candidate data array
            this.LCChangeCandidateData = [];
            electedCandidates.forEach((candidate, index) => {
              const partyName = this.lcUtils.normalizePartyName(candidate.groupFullname);
              const staticParty = staticParties.find(p => 
                this.lcUtils.normalizePartyName(p.partyName) === partyName ||
                p.partyName.toLowerCase().includes(partyName.toLowerCase()) ||
                partyName.toLowerCase().includes(p.partyName.toLowerCase())
              );
              
              this.LCChangeCandidateData.push({
                candidateName: candidate.fullname,
                party: staticParty?.partyName || partyName,
                colourCode: staticParty?.colourCode || this.lcUtils.getDefaultColorForParty(partyName),
                orderElected: index + 1,
                countNumber: this.lcUtils.numberWithCommas(candidate.countNumber),
                votes: this.lcUtils.numberWithCommas(candidate.votesEnd)
              });
            });
            
            console.log('Loaded elected candidates:', this.LCChangeCandidateData);
            
            // Finalize data source
            this.finalizeDataSource();
          } else {
            console.warn('No final candidate results found in CSV');
            this.finalizeDataSource();
          }
        },
        (error) => {
          console.error('Error loading final candidate results from CSV:', error);
          this.finalizeDataSource();
        }
      );
  }



  isNumberType = (val: number | string): boolean => typeof val === 'number';

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
